/**
 * Trade-to-chart navigation.
 *
 * The Strategy Tester's per-trade bars and List-of-Trades table both can
 * answer "show me THIS trade on the chart". TradingView's dock sends a
 * message to the chart to scroll it to the trade's bar and highlight it.
 *
 * One BroadcastChannel per app session so pop-out windows and the main
 * window stay in sync.
 */
import { useEffect, useRef } from "react";

const CHANNEL = "tt-trade-focus";

export interface TradeFocusMessage {
  /** Epoch seconds (unix) of the trade's entry or exit timestamp. */
  time: number;
  /** entry | exit — the chart may draw a different marker for each. */
  side: "entry" | "exit";
  /** For the chart's own disambiguation, which trade this was (1-based). */
  tradeNumber: number;
}

/** Call to broadcast "focus the chart on this trade". */
export function useTradeFocuser(): (msg: TradeFocusMessage) => void {
  const chRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    const ch = new BroadcastChannel(CHANNEL);
    chRef.current = ch;
    return () => { ch.close(); chRef.current = null; };
  }, []);
  return (msg) => { chRef.current?.postMessage(msg); };
}

/** Subscribe to trade-focus events — for the chart component. */
export function useTradeFocusListener(cb: (msg: TradeFocusMessage) => void): void {
  const cbRef = useRef(cb);
  cbRef.current = cb;
  useEffect(() => {
    const ch = new BroadcastChannel(CHANNEL);
    const handler = (ev: MessageEvent<TradeFocusMessage>) => { cbRef.current(ev.data); };
    ch.addEventListener("message", handler);
    return () => {
      ch.removeEventListener("message", handler);
      ch.close();
    };
  }, []);
}
